/**
 * Identifier helpers shared by the API, the admin intake screen, and the tests.
 * Validation must be identical on both sides or the scan-loop UI will accept
 * IMEIs the server then rejects.
 */
/** Luhn checksum, used by IMEIs (and card BINs, which we never store). */
export function luhnIsValid(digits) {
    if (!/^\d+$/.test(digits))
        return false;
    let sum = 0;
    let double = false;
    for (let i = digits.length - 1; i >= 0; i--) {
        let d = digits.charCodeAt(i) - 48;
        if (double) {
            d *= 2;
            if (d > 9)
                d -= 9;
        }
        sum += d;
        double = !double;
    }
    return sum % 10 === 0;
}
/** Why an IMEI fails validation, in words the intake screen can show.
 *  Returns null when the IMEI is valid. */
export function imeiRejectionReason(value) {
    if (!/^\d+$/.test(value))
        return 'Not an IMEI — digits only';
    if (value.length !== 15)
        return `Not an IMEI — ${value.length} digits, an IMEI has 15`;
    if (!luhnIsValid(value))
        return 'Not a valid IMEI — check digit fails (mistyped digit?)';
    return null;
}
/** A valid IMEI is exactly 15 digits and passes Luhn. */
export function isValidImei(value) {
    return imeiRejectionReason(value) === null;
}
/**
 * Splits a pasted list or an uploaded/OCR'd block of text into tokens and
 * keeps only the digits of each one, rather than trusting column position or
 * a fixed delimiter. Shared by the IMEI and ICCID extractors below — vendor
 * exports and photographed labels have the same shape of mess (one value per
 * line, a CSV with extra columns, a "IMEI:"/"ICCID:" prefix, stray headers).
 *
 * A token whose digit count is close to the target length is still returned
 * as a candidate: a mis-scanned or truncated value should show up as an
 * invalid row for a human to look at, not vanish silently. Anything else
 * (header words, prices, dates) is dropped and counted in `skipped`.
 */
function extractDigitTokens(text, minLen, maxLen) {
    const candidates = [];
    let skipped = 0;
    for (const raw of text.split(/[\s,;]+/)) {
        const token = raw.replace(/^["']+|["']+$/g, '');
        if (!token)
            continue;
        const digits = token.replace(/\D/g, '');
        if (digits.length >= minLen && digits.length <= maxLen) {
            candidates.push(digits);
        }
        else {
            skipped++;
        }
    }
    return { candidates, skipped };
}
/** Pulls IMEI-shaped (10-17 digit) tokens out of a pasted list or an uploaded
 *  shipment file. See extractDigitTokens for how tokens are found. */
export function extractImeiCandidates(text) {
    return extractDigitTokens(text, 10, 17);
}
/** Why an ICCID fails validation, in words a screen can show.
 *  Returns null when the ICCID is valid. */
export function iccidRejectionReason(value) {
    if (!/^\d+$/.test(value))
        return 'Not an ICCID — digits only';
    if (value.length < 19 || value.length > 20) {
        return `Not an ICCID — ${value.length} digits, an ICCID has 19-20`;
    }
    if (!luhnIsValid(value))
        return 'Not a valid ICCID — check digit fails (mistyped digit?)';
    return null;
}
/** A valid ICCID is 19-20 digits and passes Luhn. */
export function isValidIccid(value) {
    return iccidRejectionReason(value) === null;
}
/** True when the value is ICCID-shaped (19-20 digits) and fails only the Luhn
 *  check digit — the one failure an administrator may deliberately accept at
 *  intake, because some carriers really print non-Luhn ICCIDs (t0918). A
 *  wrong length or a non-digit character is never overridable, and neither
 *  is an IMEI — this only ever applies to ICCIDs. */
export function iccidFailsChecksumOnly(value) {
    return /^\d{19,20}$/.test(value) && !luhnIsValid(value);
}
/** Pulls ICCID-shaped (16-22 digit) tokens out of a pasted list or a
 *  photographed SIM card. Near-misses (16-18 or 21-22 digits) still surface
 *  as candidates for the same reason as extractImeiCandidates: a human
 *  should see the mis-read, not have it vanish. */
export function extractIccidCandidates(text) {
    return extractDigitTokens(text, 16, 22);
}
/**
 * Normalize user/scanner input to E.164 for US numbers.
 * Accepts "718-555-0143", "(718) 555 0143", "17185550143", "+17185550143".
 * Returns null when it is not a plausible US number — callers decide the error.
 */
export function normalizeUsPhone(input) {
    const digits = input.replace(/\D/g, '');
    if (digits.length === 10)
        return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1'))
        return `+${digits}`;
    return null;
}
/** Display form for a stored E.164 US number: +17185550143 -> (718) 555-0143 */
export function formatUsPhone(e164) {
    const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}
/** Money is integer cents everywhere. Format only at the edges. */
export function formatMoney(cents, currency = 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}
export const UNIT_IDENTIFIER_LABELS = {
    imei: 'IMEI',
    iccid: 'ICCID',
    license: 'License number',
};
/** Why a license number fails validation, in words a screen can show.
 *  Letters, digits and `.` `_` `-` are allowed (must start with a letter or
 *  digit), 4-64 characters, no spaces. Case is kept exactly as typed.
 *  Returns null when the license number is valid. */
export function licenseRejectionReason(value) {
    if (value.length < 4 || value.length > 64) {
        return `Not a license number — ${value.length} characters, a license number has 4-64`;
    }
    if (!/^[A-Za-z0-9]/.test(value)) {
        return 'Not a license number — must start with a letter or digit';
    }
    if (!/^[A-Za-z0-9._-]+$/.test(value)) {
        return 'Not a license number — letters, digits, "-", "_" and "." only (no spaces)';
    }
    return null;
}
/** A valid license number is 4-64 letters/digits (plus . _ -). */
export function isValidLicense(value) {
    return licenseRejectionReason(value) === null;
}
/** A phone's units are keyed by IMEI, a SIM's by ICCID, a digital license's by
 *  its license number. */
export function unitIdentifierKind(type) {
    switch (type) {
        case 'sim':
            return 'iccid';
        case 'digital_license':
            return 'license';
        default:
            return 'imei';
    }
}
/** Dispatches to `imeiRejectionReason`/`iccidRejectionReason`/
 *  `licenseRejectionReason` by kind, so a caller validating a unit-tracked
 *  product's identifier doesn't need its own `if (kind === ...)` — receiving,
 *  correcting, and loading a slot all use this the same way. */
export function identifierRejectionReason(kind, value) {
    if (kind === 'license')
        return licenseRejectionReason(value);
    return kind === 'iccid' ? iccidRejectionReason(value) : imeiRejectionReason(value);
}
//# sourceMappingURL=identifiers.js.map